import { useState, useRef } from 'react'
import { Calendar, X } from 'lucide-react'
import Popover from './Popover'

interface DateInputProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
  // Set when a Field labels this control, so clicking the label focuses it.
  id?: string
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

export default function DateInput({ value, onChange, disabled, className = '', id }: DateInputProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const today = new Date()
  const parsed = value ? new Date(value.length === 10 ? value + 'T00:00:00' : value) : null
  const [view, setView] = useState(() => parsed ?? today)

  const display = parsed ? parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Select date'

  const year = view.getFullYear()
  const month = view.getMonth()
  const firstDay = new Date(year, month, 1).getDay() // 0=Sun
  const offset = isNaN(firstDay) ? 0 : (firstDay + 6) % 7 // convert to Mon-first
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const cells: (number | null)[] = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)]

  const toggle = () => {
    if (disabled) return
    // Open on the month of the current value, not wherever it was last left.
    if (!open) setView(parsed ?? today)
    setOpen(!open)
  }

  const select = (day: number) => {
    const mm = String(month + 1).padStart(2, '0')
    const dd = String(day).padStart(2, '0')
    onChange(`${year}-${mm}-${dd}`)
    setOpen(false)
  }

  const isSelected = (day: number) => parsed?.getFullYear() === year && parsed?.getMonth() === month && parsed?.getDate() === day
  const isToday = (day: number) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === day

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
        className={`w-full flex items-center gap-2 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-danone-cyan ${
          disabled
            ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
            : 'border-slate-200 bg-white text-slate-700 cursor-pointer hover:border-slate-300'
        }`}
      >
        <Calendar className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span className={`flex-1 text-left ${!parsed ? 'text-slate-400' : ''}`}>{display}</span>
        {parsed && !disabled && (
          <X className="w-3.5 h-3.5 text-slate-300 hover:text-slate-500" aria-label="Clear date" onClick={e => { e.stopPropagation(); onChange('') }} />
        )}
      </button>

      <Popover open={open && !disabled} anchorRef={ref} onClose={() => setOpen(false)} width="content" className="p-3 w-64 overflow-y-auto" label="Choose a date">
        <div className="flex items-center justify-between mb-3">
          <button type="button" aria-label="Previous month" onClick={() => setView(new Date(year, month - 1, 1))} className="p-1 rounded hover:bg-slate-100 text-slate-500 text-sm">‹</button>
          <span className="text-sm font-semibold text-slate-800">{MONTHS[month]} {year}</span>
          <button type="button" aria-label="Next month" onClick={() => setView(new Date(year, month + 1, 1))} className="p-1 rounded hover:bg-slate-100 text-slate-500 text-sm">›</button>
        </div>
        <div className="grid grid-cols-7 mb-1">
          {DAYS.map(d => <div key={d} className="text-center text-[10px] font-semibold text-slate-400 py-0.5">{d}</div>)}
        </div>
        <div data-columns="7" className="grid grid-cols-7 gap-y-0.5">
          {cells.map((day, i) => day === null ? <div key={i} /> : (
            <button key={i} type="button" data-option aria-selected={isSelected(day)} onClick={() => select(day)}
              aria-label={new Date(year, month, day).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              className={`text-xs rounded-lg py-1.5 font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-danone-cyan ${
                isSelected(day) ? 'bg-danone-cyan text-white' :
                isToday(day) ? 'text-danone-cyan font-bold hover:bg-slate-100' :
                'text-slate-700 hover:bg-slate-100'
              }`}>
              {day}
            </button>
          ))}
        </div>
      </Popover>
    </div>
  )
}
